/**
 * The idea behind this module is to separate the concerns of the typeahead
 * initially this was inside the typeahead.js file, but since it only handles what
 * we call keyboard accesibilty, we felt it was better that this code was separated from the
 * typeahead itself, and that modularize itself for easier reading of the typeahead code.
 */

function typeaheadKeyboardHandler(config) {
  const suggestList = config.suggestList;
  const searchInput = config.searchInput;
  let selectedOptionIndex = config.selectedOptionIndex;
  const search$ = config.searchObserver;

  // reset index when a new search was performed
  search$.subscribe(() => {
    selectedOptionIndex = config.selectedOptionIndex;
  });

  /**
   * updateOptionsStatus: Adds the active class to options if they're being actively focused via keyboard.
   * remove the active class from previously selected options to avoid displaying multiple selected options.
   * @param {Number} activeOptionIndex the currently active option's index.
   */
  function updateOptionsStatus(activeOptionIndex) {
    const options = suggestList.children;
    for (let index = 0; index < options.length; index += 1) {
      if (activeOptionIndex === index) {
        options[index].classList.add('nhs-c-suggestion--hover');
        options[index].ariaSelected = true;
      } else {
        options[index].classList.remove('nhs-c-suggestion--hover');
        options[index].ariaSelected = false;
      }
    }
  }

  /**
   * setSelectedOptionInTypeahead: Update the search input with the selected option.
   * @param {Number} optionIndex Option index that is active.
   */
  function setSelectedOptionInTypeahead(optionIndex) {
    const optionSelected = suggestList.children[optionIndex];
    searchInput.value = optionSelected.getAttribute('value');
    const end = searchInput.value.length;

    // move the cursor to the end of the input.
    searchInput.setSelectionRange(end, end);
  }

  /**
   * scrollToOption: Scrolls to the option to make it visible on screen.
   * @param {Number} optionIndex Option index that will be shown.
   */
  function scrollToOption(optionIndex) {
    const optionToShow = suggestList.children[optionIndex];
    optionToShow.scrollIntoViewIfNeeded();
  }

  /**
   * upArrowHandler: Handles the up arrow key when it is pressed.
   */
  function upArrowHandler() {
    if (selectedOptionIndex < 1) {
      return;
    }

    selectedOptionIndex -= 1;
    setSelectedOptionInTypeahead(selectedOptionIndex);
    updateOptionsStatus(selectedOptionIndex);
    scrollToOption(selectedOptionIndex);
  }

  /**
   * downArrowHandler: Handles the down arrow key when it is pressed.
   */
  function downArrowHandler() {
    const optionSize = suggestList.childElementCount;
    if (optionSize < 1 || selectedOptionIndex === optionSize - 1) {
      return;
    }

    selectedOptionIndex += 1;
    setSelectedOptionInTypeahead(selectedOptionIndex);
    updateOptionsStatus(selectedOptionIndex);
    scrollToOption(selectedOptionIndex);
  }

  /**
   * closeOptions: Closes the suggestion list.
   */
  function closeOptions() {
    suggestList.innerHTML = '';
    suggestList.setAttribute('hidden', '');
    selectedOptionIndex = -1;
  }

  /**
   * enterHandler: Handles the enter key when it is pressed.
   */
  function enterHandler() {
    const currentSearch = searchInput.value;
    const suggestedOption = suggestList.querySelector(`[value="${currentSearch}"]`);
    if (suggestedOption) {
      suggestedOption.classList.add('nhs-c-suggestion--selected');
      suggestedOption.click();
      closeOptions();
    }
  }

  const keyHandlers = {
    ArrowDown: downArrowHandler,
    ArrowUp: upArrowHandler,
    Enter: enterHandler,
  };

  /**
   * keyDownHandler: Handles the all the key down events on the search input.
   * @param {Event} event Any key down event.
   */
  function keyDownHandler(event) {
    const arrowHandler = keyHandlers[event.key];
    if (arrowHandler) {
      event.preventDefault();
      arrowHandler();
    }
  }

  return keyDownHandler;
}

export default typeaheadKeyboardHandler;
